import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import PageRenderer from '../../components/common/PageRenderer';

function generateCaptcha() {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return { question: `${a} + ${b}`, answer: String(a + b) };
}

function MessageModal({ surveyor, onClose }) {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [captcha, setCaptcha] = useState(() => generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (captchaInput.trim() !== captcha.answer) {
      toast.error('Incorrect answer. Please solve the math question.');
      setCaptcha(generateCaptcha());
      setCaptchaInput('');
      return;
    }
    setSending(true);
    try {
      await api.post('/messages/submit/', {
        ...formData,
        source: 'surveyor_inquiry',
        surveyor_name: surveyor.full_name,
        surveyor_membership_number: surveyor.membership_number || '',
      });
      toast.success('Message sent successfully!');
      onClose();
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#0B5D1E] rounded-t-2xl px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold text-lg">Send a Message</h3>
            <p className="text-white/70 text-xs mt-0.5">
              Inquiry about <span className="font-semibold text-[#C8A951]">{surveyor.full_name}</span>
              {surveyor.membership_number && <span> ({surveyor.membership_number})</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white transition p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Your Name</label>
              <input type="text" required value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                className="w-full px-4 py-2.5 bg-[#F8FAFB] border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Your Email</label>
              <input type="email" required value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="w-full px-4 py-2.5 bg-[#F8FAFB] border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Subject</label>
            <input type="text" required value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="What is this regarding?"
              className="w-full px-4 py-2.5 bg-[#F8FAFB] border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none text-sm" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Message</label>
            <textarea rows={4} required value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Type your message here..."
              className="w-full px-4 py-2.5 bg-[#F8FAFB] border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none text-sm resize-none" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Security Check: What is {captcha.question}?</label>
            <input type="text" required value={captchaInput}
              onChange={(e) => setCaptchaInput(e.target.value)}
              placeholder="Enter your answer"
              className="w-full px-4 py-2.5 bg-[#F8FAFB] border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none text-sm" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" disabled={sending}
              className="flex-1 bg-[#0B5D1E] hover:bg-[#094A18] disabled:opacity-60 text-white px-4 py-2.5 rounded-lg text-sm font-bold transition-all shadow-md flex items-center justify-center gap-2">
              {sending ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</>
              ) : (
                <>Send Message <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg></>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ShortcodeFinder() {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  // Guards against out-of-order responses while typing fast.
  const requestIdRef = useRef(0);

  const runSearch = async (q) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams();
      params.append('search', q);
      const res = await api.get(`/accounts/find-surveyor/?${params}`);
      if (requestId !== requestIdRef.current) return; // stale response — a newer search is in flight
      const data = res.data.results || res.data || [];
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error(err);
      setResults([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = name.trim();
    if (q) runSearch(q);
  };

  // Auto-search as user types (debounced 400ms, min 2 chars) —
  // mirrors the Find a Surveyor box.
  useEffect(() => {
    const q = name.trim();
    if (q.length < 2) {
      if (!q) { setResults([]); setHasSearched(false); }
      return;
    }
    const timer = setTimeout(() => runSearch(q), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  return (
    <div className="bg-[#F8FAFB] rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm h-full flex flex-col">
      <h2 className="text-lg font-bold text-[#0B5D1E] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
        Find a Shortcode
      </h2>
      <p className="text-xs text-gray-500 mb-4">Enter your name to retrieve your member shortcode.</p>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Enter your full name"
            className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none transition text-sm" />
        </div>
        <button type="submit" disabled={loading || !name.trim()}
          className="bg-[#0B5D1E] hover:bg-[#094A18] disabled:opacity-60 text-white px-6 py-3.5 rounded-lg text-sm font-bold tracking-wide transition-all shadow-md flex items-center justify-center gap-2">
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Searching...</>
          ) : (
            <>Find</>
          )}
        </button>
      </form>

      <div className="mt-4 flex-1">
        {!hasSearched ? (
          <p className="text-xs text-gray-400 italic">Your shortcode will appear here.</p>
        ) : loading ? null : results.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
            No member found matching <strong>{name}</strong>.
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {results.slice(0, 5).map((m) => (
              <div key={m.membership_number || m.public_profile_slug}
                className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{m.full_name}</p>
                  <p className="text-xs text-gray-500 truncate">ID: {m.membership_number || 'N/A'}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] uppercase tracking-wide text-gray-400">Shortcode</p>
                  <p className="text-sm font-bold text-[#0B5D1E] break-all">{m.shortcode || 'N/A'}</p>
                </div>
              </div>
            ))}
            {results.length > 5 && (
              <p className="text-xs text-gray-500 italic">Showing first 5 matches. Refine your name for an exact result.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SearchAndResults() {
  const [surveyors, setSurveyors] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedSurveyor, setSelectedSurveyor] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;
  const totalPages = Math.ceil(totalCount / pageSize);

  const fetchSurveyors = async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('membership_category', category);
      params.append('page', pageNum);
      const res = await api.get(`/accounts/find-surveyor/?${params}`);
      setSurveyors(res.data.results || res.data);
      setTotalCount(res.data.count || 0);
      setPage(pageNum);
      setHasSearched(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) fetchSurveyors(1);
  };

  // Auto-search as user types (debounced 400ms, min 2 chars)
  useEffect(() => {
    if (search.trim().length < 2) {
      if (!search.trim()) { setSurveyors([]); setHasSearched(false); setTotalCount(0); }
      return;
    }
    const timer = setTimeout(() => fetchSurveyors(1), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const goToPage = (p) => {
    if (p >= 1 && p <= totalPages) fetchSurveyors(p);
  };

  return (
    <>
      {/* Search Section */}
      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <form onSubmit={handleSearch} className="bg-[#F8FAFB] rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm h-full flex flex-col">
              <h2 className="text-lg font-bold text-[#0B5D1E] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Find a Surveyor
              </h2>
              <p className="text-xs text-gray-500 mb-4">Search the directory by name, membership number, firm, or specialization.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, membership number, firm, or specialization..."
                    className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none transition text-sm" />
                </div>
                <button type="submit"
                  className="bg-[#0B5D1E] hover:bg-[#094A18] text-white px-6 py-3.5 rounded-lg text-sm font-bold tracking-wide transition-all shadow-md flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search
                </button>
              </div>
            </form>
            <ShortcodeFinder />
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="pb-16 md:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {!hasSearched ? (
            <div className="text-center py-20">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Search the Directory</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Enter a name, membership number, firm, or specialization above and click Search to find a surveyor.
              </p>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center py-20">
              <div className="spinner mb-4"></div>
              <p className="text-gray-500 text-sm">Searching directory...</p>
            </div>
          ) : surveyors.length === 0 ? (
            <div className="text-center py-20">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Surveyors Found</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                No surveyors match your current search criteria. Try adjusting your search terms or browsing all categories.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-6">
                Showing <strong className="text-gray-900">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)}</strong> of <strong className="text-gray-900">{totalCount}</strong> surveyor{totalCount !== 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {surveyors.map((s) => (
                  <div key={s.membership_number || s.public_profile_slug}
                    className="card-hover bg-[#F8FAFB] rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0B5D1E] to-[#1A7A35] text-white flex items-center justify-center text-lg font-bold shadow-md flex-shrink-0">
                          {s.full_name?.charAt(0)}
                        </div>
                        <h3 className="font-bold text-[#111827] text-base" style={{ fontFamily: 'Inter, sans-serif' }}>{s.full_name}</h3>
                      </div>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-[#0B5D1E] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0" />
                          </svg>
                          <span className="text-gray-500">ID:</span>
                          <span className="font-semibold text-[#0B5D1E]">{s.membership_number || 'N/A'}</span>
                        </div>
                      </div>
                      <button onClick={() => setSelectedSurveyor(s)}
                        className="mt-5 w-full block text-center bg-[#0B5D1E] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#094A18] transition-all shadow-sm cursor-pointer">
                        View Profile & Send Message
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button onClick={() => goToPage(page - 1)} disabled={page === 1}
                    className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || (p >= page - 2 && p <= page + 2))
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === '...' ? (
                        <span key={`dots-${idx}`} className="px-2 text-gray-400 text-sm">...</span>
                      ) : (
                        <button key={item} onClick={() => goToPage(item)}
                          className={`w-10 h-10 rounded-lg text-sm font-semibold transition ${
                            item === page
                              ? 'bg-[#0B5D1E] text-white shadow-md'
                              : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}>
                          {item}
                        </button>
                      )
                    )}
                  <button onClick={() => goToPage(page + 1)} disabled={page === totalPages}
                    className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {selectedSurveyor && (
        <MessageModal surveyor={selectedSurveyor} onClose={() => setSelectedSurveyor(null)} />
      )}
    </>
  );
}

function DefaultContent() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B5D1E]" style={{ backgroundImage: 'linear-gradient(135deg, #0B5D1E 0%, #094A18 100%)' }}>
        <div className="relative" style={{ paddingTop: 80, paddingBottom: 80, zIndex: 1 }}>
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, color: '#fff', marginBottom: 16, fontFamily: "'Playfair Display',serif" }}>
              Find a Surveyor
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Search our directory of registered surveyors in the Federal Capital Territory
            </p>
          </div>
        </div>
      </section>

      {/* Search & Results */}
      <SearchAndResults />
    </>
  );
}

export default function FindSurveyor() {
  return (
    <PageRenderer
      pageKey="find_surveyor"
      insertAfter={{
        'Find Surveyor Hero': () => <SearchAndResults />,
      }}
      fallback={<DefaultContent />}
    />
  );
}
